import { type UUID, createUuid } from "../../utils/uuid";
import {
    type PedestrianDevicesApiResource,
    type PedestrianDevicesQuery,
} from "../../domain/repositories/repository-pedestrian-devices";
import { observer } from "mobx-react";
import { action, computed, makeObservable } from "mobx";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import { type IColumn, type IObjectWithKey, SelectionMode, Selection } from "@fluentui/react";
import type { SortState } from "../../utils/sort-state";
import type { PedestrianDeviceSortKey, PedestrianDevice } from "../../api";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import React from "react";
import { defaultPageSize } from "../../utils/constants";
import { store } from "../../store";
import { ListPedestrianDeviceItemCell } from "../atoms/list-pedestrian-device-item-cell";
import type { SelectionState } from "../../utils/selection-state";

export interface ListPedestrianDevicesProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListPedestrianDeviceItem extends IObjectWithKey {
    readonly key: UUID;
}

@observer
export class ListPedestrianDevices extends React.Component<ListPedestrianDevicesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.pedestrianDevices.getSelectionState(this.listStateId);
    }

    constructor(props: ListPedestrianDevicesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.pedestrianDevices.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }
    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<PedestrianDeviceSortKey> {
        return store.serviceListStates.pedestrianDevices.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<PedestrianDevicesApiResource> {
        return store.serviceListStates.pedestrianDevices.getPaginationState(this.listStateId);
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [];
        columns.push({
            fieldName: "label",
            name: store.i18n.t("listPedestrianDevices.column.label.name"),
            key: "label",
            minWidth: 100,
            maxWidth: 200,
        });
        columns.push({
            fieldName: "hardwareSerialNumber",
            name: store.i18n.t("listPedestrianDevices.column.hardwareSerialNumber.name"),
            key: "hardwareSerialNumber",
            minWidth: 100,
            maxWidth: 200,
        });
        columns.push({
            fieldName: "networkId",
            name: store.i18n.t("listPedestrianDevices.column.networkId.name"),
            key: "networkId",
            minWidth: 100,
            maxWidth: 200,
        });
        return columns;
    }

    @computed private get pedestrianDevices(): (PedestrianDevice | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryPedestrianDevices.byId(id));
        } else {
            return store.repositoryPedestrianDevices.byQuery(
                this.query,
                this.paginationState.pagination,
            );
        }
    }

    @computed private get items(): (ListPedestrianDeviceItem | undefined)[] {
        return this.pedestrianDevices.map((pedestrianDevice) => {
            if (!pedestrianDevice) {
                return;
            }
            return {
                key: pedestrianDevice.id,
            };
        });
    }

    @computed private get query(): PedestrianDevicesQuery {
        const query = {
            pageSize: defaultPageSize,
            ...this.sortState.query,
        } as PedestrianDevicesQuery;
        return query;
    }

    private renderItem(pedestrianDeviceId: UUID, column: IColumn): JSX.Element {
        return (
            <ListPedestrianDeviceItemCell pedestrianDeviceId={pedestrianDeviceId} column={column} />
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.pedestrianDevices.isInitialized(this.listStateId)) {
            return <></>;
        }

        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...this.props}
                    selection={this.selection}
                    selectionMode={SelectionMode.single}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    // Disable right-click to stay consistent with other lists
                    onItemContextMenu={() => {}}
                    onRenderItemColumn={(item: ListPedestrianDeviceItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
